import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Høyfjellet cart upsells use the theme's native cart-update flow.
// Event delegation keeps the buttons working even when Shopify morphs the cart drawer HTML.
if (!window.__hfCartUpsellBound) {
  window.__hfCartUpsellBound = true;

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-hf-cart-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();

    const wrapper = button.closest('[data-hf-cart-upsell]');
    const select = wrapper?.querySelector('[data-hf-buff-select]');
    const variantId = select?.value || button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Legger til…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      for (const section of document.querySelectorAll('cart-items-component')) {
        const sectionId = section.dataset.sectionId;
        if (sectionId) formData.append('sections', sectionId);
      }

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      const addData = await response.json();

      if (!response.ok || addData.status) {
        throw new Error(addData.description || addData.message || 'Kunne ikke legge varen i kurven');
      }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Kunne ikke oppdatere handlekurven');
      const cart = await cartResponse.json();

      button.dispatchEvent(
        new CartAddEvent(cart, `hf-cart-upsell-${variantId}`, {
          didError: false,
          source: 'hf-cart-upsell',
          itemCount: cart.item_count,
          productId: addData.product_id,
          sections: addData.sections || {},
        })
      );
    } catch (error) {
      console.error('[Høyfjellet cart upsell]', error);
      button.disabled = false;
      button.textContent = originalText;
    }
  });
}
